<script setup lang="ts">
import type { MachineryCategories, MachineryRubrics, UpdateOrCreateMachineryType } from '~/types'

const { openMachineryTypePopup: { data: popupData, close } } = useGlobalOpeners()

const machineryCreateOrEditPopup = useRouteQueryAsObject('machineryCreateOrEditPopup')

const { $trpc, queryClient, useMutation, makeTrpcErrorToast } = useMutationHelpers()
const notification = useNotification()

const isCreatedTypeIdRequired = computed(() => popupData.value?.mode === 'create' ? popupData.value.isCreatedTypeIdRequired : false)
const id = computed(() => popupData.value?.mode === 'update' ? popupData.value.typeId : undefined)
const enabledMachineryType = computed(() => !!id.value)

const { machineryType: queryMachineryType, machineryDrive: queryMachineryDrive, trackerSensor: queryTrackerSensor } = useQuery()
const { data: machineryType, isLoading: isLoadingMachineryType } = queryMachineryType.byIdOrThrow(id, enabledMachineryType)
const { data: machineryDrives } = queryMachineryDrive.all(false)
const { data: trackerSensorConfigOptions } = queryTrackerSensor.getConfigurationOptions()

const update = useMutation({
  mutationFn: $trpc.machineryType.update.mutate,
  onError: makeTrpcErrorToast(notification, { description: 'Der Typ konnte nicht aktualisiert werden' }),
  onSuccess: async () => {
    await queryClient.invalidateQueries({ queryKey: ['machineryTypes'] })
    await queryClient.invalidateQueries({ queryKey: ['machines'] })
    closePopup()
    notification.success({ title: 'Die Typ wurde erfolgreich aktualisiert', duration: 4500 })
  },
})

const create = useMutation({
  mutationFn: $trpc.machineryType.create.mutate,
  onError: makeTrpcErrorToast(notification, { description: 'Der Typ konnte nicht erstellt werden' }),
  onSuccess: async (type) => {
    await queryClient.invalidateQueries({ queryKey: ['machineryTypes'] })
    if (isCreatedTypeIdRequired.value) {
      closePopup(type.id)
    } else {
      closePopup()
    }
    notification.success({ title: 'Der Typ wurde erfolgreich erstellt', duration: 4500 })
  },
})

const payload = computed((): UpdateOrCreateMachineryType | undefined => {
  if (!popupData.value) {
    return undefined
  }

  if (popupData.value.mode === 'create') {
    return {
      mode: 'create',
      data: {
        name: '',
        category: popupData.value.category ?? 'forklift',
        documentDataSheetFiles: [],
        documentManualFiles: [],
        documentVideoFiles: [],
        homepageImages: [],
        documentExternalVideoFiles: [],
      },
    }
  }

  if (machineryType.value && !isLoadingMachineryType.value) {
    return {
      mode: 'update',
      data: {
        ...machineryType.value,
        machineryRubric: machineryType.value.machineryRubric as MachineryRubrics ?? undefined,
        category: machineryType.value.category as MachineryCategories,
      },
    }
  }
})

function saveCreateOrEditForm(payload: UpdateOrCreateMachineryType) {
  if (payload.mode === 'create') {
    return create.mutateAsync(payload.data)
  } else {
    return update.mutateAsync(payload.data)
  }
}

function closePopup(createdTypeId?: string) {
  if (createdTypeId) {
    machineryCreateOrEditPopup.value = {
      ...machineryCreateOrEditPopup.value,
      typeId: createdTypeId,
    }
  }

  close()
}
</script>

<template>
  <ThePopup v-if="payload" :show="Boolean(payload)" title="Gerätetyp-Anlage" @close="closePopup">
    <MachineryTypeCreationCreateOrEditForm
      :payload="payload"
      :machinery-drives="machineryDrives ?? []"
      :tracker-sensor-configuration-options="trackerSensorConfigOptions ?? []"
      @save="saveCreateOrEditForm"
    />
  </ThePopup>
</template>
